import React, { useState, useRef, useEffect } from 'react';
import { AutoComplete, Button, Spin } from 'antd';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { Typography } from '../design-system/typography';
import { colors, spacing } from '../design-system/tokens';
import { suppliersApi, buyersApi, sellersApi } from '../../services/api';

export interface Company {
  id: string;
  name: string;
  code: string;
  shortCode: string;
  address: {
    street: string;
    city: string;
    state?: string;
    postalCode: string;
    country: string;
  };
  type: 'supplier' | 'buyer' | 'seller';
}

interface CompanyAutocompleteProps {
  value?: string;
  onChange?: (value: string, company?: Company) => void;
  placeholder?: string;
  companyType: 'supplier' | 'buyer' | 'seller';
  onCreateNew?: () => void;
  disabled?: boolean;
}

export const CompanyAutocomplete: React.FC<CompanyAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Select company...',
  companyType,
  onCreateNew,
  disabled = false
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Map API response from /companies endpoint to Company interface
  const mapApiResponse = (data: any): Company => {
    const addressStr = data.address || '';
    const country = data.country || '';

    return {
      id: data.id,
      name: data.name,
      code: data.shortCode,
      shortCode: data.shortCode,
      address: {
        street: addressStr, // Full address string from database
        city: '',
        state: '',
        postalCode: '',
        country: country
      },
      type: data.type?.toLowerCase() as 'supplier' | 'buyer' | 'seller'
    };
  };

  // Fetch companies from API
  const fetchCompanies = async (searchText: string) => {
    if (!searchText || searchText.length < 2) {
      setFilteredCompanies([]);
      setHasSearched(false);
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      // Use type-specific endpoints (all return same structure)
      let response: any;
      let items: any[];

      if (companyType === 'seller') {
        response = await sellersApi.list({ search: searchText, active: true, limit: 20 });
        items = (response as any).sellers || [];
      } else if (companyType === 'supplier') {
        response = await suppliersApi.list({ search: searchText, active: true, limit: 20 });
        items = (response as any).suppliers || [];
      } else {
        response = await buyersApi.list({ search: searchText, active: true, limit: 20 });
        items = (response as any).buyers || [];
      }

      const mapped = items.map((item: any) => mapApiResponse(item));
      setFilteredCompanies(mapped);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      setFilteredCompanies([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clean up debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);

    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new debounced search
    debounceRef.current = setTimeout(() => {
      fetchCompanies(searchText);
    }, 300);
  };

  const handleSelect = (selectedValue: string) => {
    const selectedCompany = filteredCompanies.find(company =>
      `${company.name} - ${formatAddress(company.address)}` === selectedValue
    );

    if (onChange) {
      onChange(selectedValue, selectedCompany);
    }
  };

  const handleChange = (newValue: string) => {
    // Always update the value - allows typing, backspace, and clearing
    if (onChange) {
      // If the new value matches a company in our filtered list, pass that company
      const matchedCompany = filteredCompanies.find(company =>
        `${company.name} - ${formatAddress(company.address)}` === newValue
      );
      onChange(newValue, matchedCompany);
    }
  };

  const formatAddress = (address: Company['address']): string => {
    const parts = [];
    // street contains the full address string from database
    if (address.street) parts.push(address.street);
    // Add country if not already included in the address string
    if (address.country && !address.street?.toLowerCase().includes(address.country.toLowerCase())) {
      parts.push(address.country);
    }
    return parts.join(', ') || 'No address on file';
  };

  const options = filteredCompanies.map(company => ({
    value: `${company.name} - ${formatAddress(company.address)}`,
    label: (
      <div style={{ padding: `${spacing.xs} 0` }}>
        <Typography variant="label" style={{ color: colors.text.primary }}>
          {company.name}
          <span style={{
            marginLeft: spacing.sm,
            color: colors.text.tertiary,
            fontSize: '12px',
            fontWeight: 'normal'
          }}>
            ({company.code})
          </span>
        </Typography>
        <Typography
          variant="value"
          style={{
            color: colors.text.secondary,
            fontSize: '12px',
            marginTop: spacing.xs
          }}
        >
          {formatAddress(company.address)}
        </Typography>
      </div>
    )
  }));

  return (
    <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-start' }}>
      <AutoComplete
        style={{ flex: 1 }}
        value={value}
        placeholder={placeholder}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onChange={handleChange}
        options={options}
        disabled={disabled}
        allowClear
        filterOption={false}
        notFoundContent={
          isLoading ? (
            <div style={{
              padding: spacing.md,
              textAlign: 'center',
              color: colors.text.secondary
            }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
              <Typography variant="value" style={{ marginLeft: spacing.sm }}>
                Searching...
              </Typography>
            </div>
          ) : searchValue && searchValue.length >= 2 && hasSearched ? (
            <div style={{
              padding: spacing.md,
              textAlign: 'center',
              color: colors.text.secondary
            }}>
              <Typography variant="value">
                No companies found matching "{searchValue}"
              </Typography>
              {onCreateNew && (
                <Button
                  type="link"
                  size="small"
                  onClick={onCreateNew}
                  style={{
                    padding: `${spacing.xs} 0`,
                    height: 'auto',
                    color: colors.status.tertiary
                  }}
                >
                  Create new {companyType}
                </Button>
              )}
            </div>
          ) : searchValue && searchValue.length < 2 ? (
            <div style={{
              padding: spacing.md,
              textAlign: 'center',
              color: colors.text.secondary
            }}>
              <Typography variant="value">
                Type at least 2 characters to search
              </Typography>
            </div>
          ) : null
        }
      />

      {onCreateNew && (
        <Button
          icon={<PlusOutlined />}
          onClick={onCreateNew}
          disabled={disabled}
          style={{
            borderColor: colors.status.tertiary,
            color: colors.status.tertiary
          }}
        >
          New
        </Button>
      )}
    </div>
  );
};
